import { FC, useMemo, useCallback } from 'react'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { CRYPTO_REWARDS_SELL_MODE_STEP_CRYPTO_WALLET, CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { getCryptoRewardsPaneStateSellModeOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { cryptoWalletNotEmptyTickerSelector } from 'components/features/rewards/rewardsPane/store/selectors/cryptoWallet'
import { CryptoRewardsSellModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateSellMode'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { SelCryptoRow } from 'components/features/rewards/rewardsPane/containers/CryptoRewardsSellPane/CryptoRewardsSellCryptoWalletStep/SelCryptoRow'

const STEP_TITLE = 'Sell Your Crypto Investments'
const STEP_SUBTITLE = 'Which investment do you want to sell?'


type CryptoRewardsSellCryptoWalletStepProps = {
  onClose: () => void
}

export const CryptoRewardsSellCryptoWalletStep: FC<CryptoRewardsSellCryptoWalletStepProps> = (props) => {
  const { onClose } = props

  const isMetaLoaded = useSimpleGuard(cryptoMetaGuard, {}, 'CryptoRewardsSellCryptoWalletStep')

  const sellPaneStateOrNull: CryptoRewardsSellModeSteps | null = useStoreSelector(getCryptoRewardsPaneStateSellModeOrNullSelector)
  const backStepOrNull = sellPaneStateOrNull?.backStep ?? null
  const cancelButtonName = backStepOrNull ? 'Back' : 'Cancel'
  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE)
  const handleCancelPressed = useCallback((): void => {
    if (backStepOrNull) {
      modifyPaneState({
        step: backStepOrNull,
        backStep: CRYPTO_REWARDS_SELL_MODE_STEP_CRYPTO_WALLET,
      })
      return
    }
    onClose()
  }, [backStepOrNull, onClose, modifyPaneState])

  const walletTickers = useStoreSelector(cryptoWalletNotEmptyTickerSelector)

  const handleSelect = useCallback((id: CryptoCurrencyId) => {
    modifyPaneState({
      step: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
      fields: { cryptoCurrencyIDChosenForSelling: id },
      backStep: CRYPTO_REWARDS_SELL_MODE_STEP_CRYPTO_WALLET,
    })
  }, [modifyPaneState])

  const child = useMemo(() => {
    if (!isMetaLoaded) {
      return (
        <LoadingOverlay />
      )
    }

    return walletTickers.map((ticker) => (
      <SelCryptoRow
        key={ ticker }
        ticker={ ticker }
        onSelect={ () => handleSelect(ticker) }
      />
    ))
  }, [isMetaLoaded, walletTickers, handleSelect])


  return (
    <CryptoRewardsPanePreset
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      progressBarPercent={ 5 }

      cancelName={ cancelButtonName }
      onFooterCancel={ handleCancelPressed }
      onCancel={ handleCancelPressed }
    >
      { child }
    </CryptoRewardsPanePreset>
  )
}
